import type {
  Post,
  PostListFilterByInput,
} from '@tribeplatform/gql-client/types'
import {
  PostListFilterByEnum,
  PostListFilterByOperator,
  PostTypeContext,
} from '@tribeplatform/gql-client/types'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useNetwork, usePosts } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { useQuery } from '../../../lib/useQuery'
import { MESSAGING_POST_TYPE_SLUG } from '../../../utils/messaging.utils'
import { convertToStatic } from '../../Views/utils'
import { staticFieldsFactory } from '../config'
import { useCustomFields } from '../hooks/useCustomFields'
import { useShowMoreButton } from '../hooks/useShowMoreButton'
import type { PostsBlockView } from '../types'
import { getFilterKeyString, getFilterOperator } from '../utils'
import { generateGqlFields } from '../utils/generateGqlFields'
import { EmptyState as PostsEmptyState } from './EmptyState'
import { GenericPostsList } from './GenericPostsList'
import { LoadingState } from './LoadingState'

interface Props {
  view: PostsBlockView
  activeFieldId: string
  preview: boolean
}
export const NetworkPostsList = ({
  view: {
    sort: orderBy,
    reverseSort,
    filterBy,
    limit,
    style,
    fields,
    postTypeId: id,
    postTypeSlug,
    gallerySize,
    showDivider,
    showMore,
    linkTo,
  },
  activeFieldId,
  preview,
}: Props) => {
  const staticFields = useStaticIntl(staticFieldsFactory)
  const {
    data: { postTypes },
  } = useNetwork()
  const { tag_id: tagId } = useQuery()

  const { customFields, postTypeId } = useCustomFields({
    id,
    slug: postTypeSlug,
  })

  const postsFilterBy =
    filterBy.map<PostListFilterByInput>(({ key, operator, value }) => {
      return {
        key: PostListFilterByEnum.publishedAt,
        keyString: getFilterKeyString(key),
        operator: getFilterOperator(key, operator),
        value,
      }
    }) ?? []

  if (tagId) {
    postsFilterBy.push({
      key: PostListFilterByEnum.publishedAt,
      keyString: 'tagIds',
      operator: PostListFilterByOperator.contains,
      value: JSON.stringify(tagId),
    })
  }

  const mergedStaticFields = convertToStatic(
    fields,
    staticFields,
    customFields ?? [],
  )

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePosts({
      variables: {
        limit,
        orderByString: orderBy,
        reverse: reverseSort ?? true,
        filterBy: postsFilterBy,
        postTypeIds: postTypeId
          ? [postTypeId]
          : postTypes
              ?.filter(
                postType =>
                  postType?.context === PostTypeContext.post &&
                  postType.slug !== MESSAGING_POST_TYPE_SLUG,
              )
              ?.map(postType => postType.id),
      },
      fields: generateGqlFields({ staticFields: mergedStaticFields, style }),
      useInfiniteQueryOptions: {
        refetchOnMount: 'always',
      },
    })

  const { nodes: posts } = simplifyPaginatedResult(data) as { nodes: Post[] }

  const showMoreButton = useShowMoreButton({
    style,
    gallerySize,
    showMore,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  })

  if (!isLoading && data && posts && posts.length === 0) {
    return <PostsEmptyState />
  }

  if (isLoading || !data) {
    return (
      <LoadingState
        count={limit}
        style={style}
        gallerySize={gallerySize}
        showDivider={showDivider}
      />
    )
  }

  return (
    <GenericPostsList
      style={style}
      posts={posts}
      fields={mergedStaticFields}
      gallerySize={gallerySize}
      showDivider={showDivider}
      activeFieldId={activeFieldId}
      showMoreButton={showMoreButton}
      linkTo={linkTo}
      preview={preview}
    />
  )
}
